'use client';
import { PrivacySection } from "./ui";
import styles from './Privacy.module.scss';
import { useState, useMemo } from "react";
import { SECTIONS, DATA_CATEGORIES } from "./constants";

export function Privacy() {
  const [openId, setOpenId] = useState<string | null>('general');

  const toggle = (id: string) =>
    setOpenId((prev) => (prev === id ? null : id));

  const toc = useMemo(
    () => (
      <nav className={styles.toc} aria-label="Содержание">
        <div className={styles.tocTitle}>Содержание</div>
        <ul className={styles.tocList}>
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  setOpenId(s.id);
                  document
                    .getElementById(s.id)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    ),
    [],
  );

  return (
    <div className={styles.root}>
      <header className={styles.hero}>
        <div className={styles.heroInner}>
          <span className={styles.eyebrow}>Правовая информация</span>
          <h1 className={styles.title}>Политика конфиденциальности</h1>
          <p className={styles.lead}>
            Настоящая Политика описывает, какие персональные данные мы собираем, с какими целями
            и на каких правовых основаниях обрабатываем, кому передаём и как защищаем.
          </p>
        </div>
      </header>

      <main className={styles.content}>
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{toc}</aside>

          <div className={styles.introSections}>
            {/* 1 */}
            <PrivacySection id="general" title="1. Общие положения" openId={openId} onToggle={toggle}>
              <p>
                Настоящая Политика конфиденциальности (далее — «Политика») определяет порядок
                обработки и защиты персональных данных пользователей сервиса{' '}
                <b>Canto Projects</b>, доступного по адресу <b>thelightofempire.com</b>{' '}
                (далее — «Сервис»).
              </p>
              <p>
                Политика разработана в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ
                «О персональных данных», а для пользователей из ЕС — в соответствии с Регламентом
                (ЕС) 2016/679 (GDPR).
              </p>
              <p>
                Используя Сервис, вы подтверждаете, что ознакомлены с настоящей Политикой
                и даёте согласие на обработку персональных данных на описанных ниже условиях.
              </p>
            </PrivacySection>

            {/* 2 */}
            <PrivacySection id="operator" title="2. Оператор персональных данных" openId={openId} onToggle={toggle}>
              <p>
                Оператором персональных данных является: 
              </p>
              <ul className={styles.contacts}>
                <li><span>Наименование:</span> Canto Projects</li>
                {/* <li><span>ИНН:</span> [0000000000]</li>
                <li><span>Адрес:</span> г. Москва, ул. Примерная, д. 1</li> */}
                <li><span>VK:</span> <a href="https://vk.ru/thelightofempire">https://vk.ru/thelightofempire</a></li>
                {/* <li><span>Телефон:</span> <a href="tel:+70000000000">+7 (000) 000-00-00</a></li> */}
              </ul>
            </PrivacySection>

            {/* 3 */}
            <PrivacySection id="composition" title="3. Состав обрабатываемых данных" openId={openId} onToggle={toggle}>
              <p>Мы можем обрабатывать следующие категории персональных данных:</p>
              <div className={styles.cards}>
                {DATA_CATEGORIES.map((c) => (
                  <article key={c.badge} className={styles.card}>
                    <div className={styles.cardBadge}>{c.badge}</div>
                    <p>{c.text}</p>
                  </article>
                ))}
              </div>
              <p className={styles.warning}>
                Мы не обрабатываем специальные категории персональных данных (о расовой,
                национальной принадлежности, политических взглядах, религиозных убеждениях,
                состоянии здоровья, интимной жизни) и биометрические данные, за исключением
                случаев, прямо предусмотренных законом.
              </p>
            </PrivacySection>

            {/* 4 */}
            <PrivacySection id="purposes" title="4. Цели обработки" openId={openId} onToggle={toggle}>
              <p>Персональные данные обрабатываются в следующих целях:</p>
              <ul>
                <li>регистрация и авторизация пользователя в Сервисе;</li>
                <li>предоставление функциональных возможностей Сервиса;</li>
                <li>исполнение договора и расчётов (при наличии платных услуг);</li>
                <li>информирование о работе Сервиса, изменениях условий, ответах на запросы;</li>
                <li>улучшение качества Сервиса, проведение аналитики в обезличенном виде;</li>
                <li>направление рекламных и информационных сообщений (при отдельном согласии);</li>
                <li>соблюдение требований законодательства (налогового, бухгалтерского и др.).</li>
              </ul>
            </PrivacySection>

            {/* 5 */}
            <PrivacySection id="legal" title="5. Правовые основания" openId={openId} onToggle={toggle}>
              <p>Обработка персональных данных осуществляется на основании:</p>
              <ul>
                <li>согласия субъекта персональных данных (п. 1 ч. 1 ст. 6 152-ФЗ);</li>
                <li>исполнения договора, стороной которого является субъект (п. 5 ч. 1 ст. 6 152-ФЗ);</li>
                <li>исполнения обязанностей, возложенных на оператора законом (п. 2 ч. 1 ст. 6 152-ФЗ);</li>
                <li>законного интереса оператора в обеспечении работы Сервиса;</li>
                <li>для пользователей из ЕС — ст. 6 GDPR (согласие, исполнение договора, законный интерес).</li>
              </ul>
            </PrivacySection>

            {/* 6 */}
            <PrivacySection id="methods" title="6. Порядок и способы обработки" openId={openId} onToggle={toggle}>
              <p>
                Обработка персональных данных осуществляется как с использованием средств
                автоматизации, так и без их использования (смешанная обработка).
              </p>
              <p>Действия с персональными данными включают:</p>
              <ul>
                <li>сбор, запись, систематизацию, накопление, хранение;</li>
                <li>уточнение (обновление, изменение), извлечение, использование;</li>
                <li>передачу (предоставление, доступ), блокирование, удаление, уничтожение.</li>
              </ul>
              <p>
                Обработка осуществляется в объёме, необходимом для достижения указанных целей,
                и не допускает избыточности по отношению к ним.
              </p>
            </PrivacySection>

            {/* 7 */}
            <PrivacySection id="cookies" title="7. Файлы Cookie и аналитика" openId={openId} onToggle={toggle}>
              <p>
                Для работы Сервиса, анализа поведения пользователей и улучшения качества услуг
                мы используем файлы cookie и аналогичные технологии. Подробные сведения — в{' '}
                <a href="/cookie">Политике использования Cookie</a>.
              </p>
              <p>Мы используем следующие категории cookie:</p>
              <ul>
                <li><b>Строго необходимые</b> — обеспечивают работу Сервиса, не требуют согласия;</li>
                <li><b>Функциональные</b> — запоминают настройки и предпочтения;</li>
                <li><b>Аналитические</b> — Google Analytics, Яндекс.Метрика (в обезличенном виде);</li>
                <li><b>Маркетинговые</b> — таргетинг рекламы, устанавливаются только с согласия.</li>
              </ul>
              <p>Вы можете управлять cookie через настройки браузера или cookie-баннер на Сайте.</p>
            </PrivacySection>

            {/* 8 */}
            <PrivacySection id="transfer" title="8. Передача третьим лицам" openId={openId} onToggle={toggle}>
              <p>Персональные данные могут передаваться:</p>
              <ul>
                <li>хостинг-провайдерам и облачным сервисам, обеспечивающим работу Сервиса;</li>
                <li>поставщикам аналитических и маркетинговых услуг (Google, Яндекс);</li>
                <li>платёжным провайдерам при проведении расчётов;</li>
                <li>контрагентам, оказывающим услуги по договору с оператором;</li>
                <li>государственным органам — по мотивированным запросам в порядке, установленном законом.</li>
              </ul>
              <p>
                Передача осуществляется только в объёме, необходимом для достижения целей
                обработки, на условиях соблюдения конфиденциальности и безопасности данных.
              </p>
              <p className={styles.warning}>
                Трансграничная передача персональных данных осуществляется только в страны,
                обеспечивающие адекватную защиту прав субъектов персональных данных, либо
                при наличии отдельного согласия субъекта.
              </p>
            </PrivacySection>

            {/* 9 */}
            <PrivacySection id="storage" title="9. Сроки хранения" openId={openId} onToggle={toggle}>
              <p>
                Персональные данные хранятся не дольше, чем этого требуют цели их обработки,
                если иной срок не установлен законодательством.
              </p>
              <ul>
                <li>данные аккаунта — до удаления аккаунта Пользователем или по инициативе Администрации;</li>
                <li>данные для расчётов и бухгалтерии — не менее 5 лет с момента прекращения обязательств;</li>
                <li>cookie — согласно срокам, указанным в Cookie-политике;</li>
                <li>логи и технические данные — не более 12 месяцев.</li>
              </ul>
            </PrivacySection>

            {/* 10 */}
            <PrivacySection id="rights" title="10. Права субъекта данных" openId={openId} onToggle={toggle}>
              <p>Вы имеете право:</p>
              <ul>
                <li>получать информацию об обработке своих персональных данных;</li>
                <li>требовать уточнения, блокирования или уничтожения данных, если они неполны, устарели, неточны или незаконно получены;</li>
                <li>отозвать согласие на обработку персональных данных;</li>
                <li>возражать против обработки в целях продвижения товаров, работ, услуг;</li>
                <li>обжаловать действия оператора в Роскомнадзор или в суд;</li>
                <li>для пользователей из ЕС — реализовывать права по ст. 15–22 GDPR (доступ, исправление, удаление, ограничение, переносимость, возражение).</li>
              </ul>
              <p>
                Для реализации прав направьте запрос на{' '}
                <a href="https://vk.ru/thelightofempire">https://vk.ru/thelightofempire</a>. Мы ответим
                в течение 30 календарных дней.
              </p>
            </PrivacySection>

            {/* 11 */}
            <PrivacySection id="security" title="11. Меры защиты" openId={openId} onToggle={toggle}>
              <p>
                Оператор принимает необходимые правовые, организационные и технические меры
                для защиты персональных данных от неправомерного доступа, уничтожения,
                изменения, блокирования, копирования, распространения:
              </p>
              <ul>
                <li>шифрование данных при передаче (HTTPS/TLS);</li>
                <li>хеширование паролей, применение политики сложности;</li>
                <li>ограничение доступа сотрудников к персональным данным по принципу минимальной необходимости;</li>
                <li>регулярный аудит безопасности и обновление инфраструктуры;</li>
                <li>резервное копирование и восстановление данных;</li>
                <li>обучение сотрудников и назначение ответственного за организацию обработки ПДн.</li>
              </ul>
            </PrivacySection>

            {/* 12 */}
            <PrivacySection id="children" title="12. Данные несовершеннолетних" openId={openId} onToggle={toggle}>
              <p>
                Сервис не предназначен для лиц младше 18 лет. Мы сознательно не собираем
                персональные данные несовершеннолетних без согласия законных представителей.
              </p>
              <p>
                Если вам стало известно, что такие данные были предоставлены без согласия,
                пожалуйста, свяжитесь с нами — мы удалим их в кратчайшие сроки.
              </p>
            </PrivacySection>

            {/* 13 */}
            <PrivacySection id="changes" title="13. Изменения политики" openId={openId} onToggle={toggle}>
              <p>
                Оператор вправе вносить изменения в настоящую Политику. Актуальная редакция
                всегда размещена на Сайте с указанием даты обновления.
              </p>
              <p>
                При существенных изменениях мы уведомим пользователей через Сервис
                или по электронной почте. Продолжая использовать Сервис после публикации
                изменений, вы соглашаетесь с новой редакцией Политики.
              </p>
            </PrivacySection>

            {/* 14 */}
            <PrivacySection id="contacts" title="14. Контакты" openId={openId} onToggle={toggle}>
              <ul className={styles.contacts}>
                <li><span>VK:</span> <a href="https://vk.ru/thelightofempire">https://vk.ru/thelightofempire</a></li>
                {/* <li><span>Телефон:</span> <a href="tel:+70000000000">+7 (000) 000-00-00</a></li>
                <li><span>Адрес:</span> г. Москва, ул. Примерная, д. 1</li> */}
              </ul>
              {/* <p>
                Ответственный за организацию обработки персональных данных —{' '}
                <b>[ФИО ответственного]</b>.
              </p> */}
            </PrivacySection>

            <div className={styles.consent}>
              <b>Ваше согласие.</b> Используя Сервис и предоставляя свои персональные данные,
              вы подтверждаете, что ознакомлены с настоящей Политикой конфиденциальности
              и согласны с условиями обработки персональных данных, изложенными в ней.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
